import { useQuery } from '@tanstack/react-query';
import { getInboxLinkAsync, type InboxLinkResult, type InboxSender } from '@/services/inbox-link';
import { isEmptyString } from '@/utils/string';
import { useSignupFlow } from '../signup-flow';
import { inboxLinkClickedProps, SignupAnalyticsEvent } from '../utils/analytics';

/**
 * The address signup verification codes are sent from — the sniper link filters
 * the inbox down to it, so a wrong value surfaces an empty inbox search.
 *
 * `services/inbox-link` exports a `SIGNUP_VERIFICATION_SENDER` default claiming
 * the same transactional message at `win@customer.io`. That one is stale; this
 * is the address production actually sends from. It stays local rather than
 * correcting the shared default, which another surface consumes for a different
 * email — see the follow-up note in the PR.
 */
export const SIGNUP_CODE_SENDER: InboxSender = { email: 'welcome@login.customer.io', domain: 'login.customer.io' };

export type InboxLink = {
  link: InboxLinkResult | null | undefined;
  isLoading: boolean;
  openInbox: () => void;
};

/**
 * Resolves a provider-specific "sniper link" that deep-links straight to the
 * verification email. Best-effort: the lookup swallows its own failures, so an
 * unresolvable provider simply means no button.
 */
export function useInboxLink(): InboxLink {
  const { formValues, trackSignupEvent } = useSignupFlow();
  const { email } = formValues;

  const { data: link, isLoading } = useQuery({
    queryKey: ['signup-inbox-link', email],
    queryFn: () => getInboxLinkAsync(email, SIGNUP_CODE_SENDER),
    enabled: !isEmptyString(email),
    staleTime: Infinity,
  });

  const openInbox = () => {
    if (link == null || isEmptyString(link.url)) {
      return;
    }
    trackSignupEvent(
      SignupAnalyticsEvent.InboxLinkClicked,
      // The domain the visitor typed, not the address the lookup resolved —
      // for a custom domain the two differ.
      inboxLinkClickedProps({ provider: link.provider, emailDomain: email.split('@')[1] }),
    );
    window.open(link.url, '_blank', 'noopener,noreferrer');
  };

  return { link, isLoading, openInbox };
}
